import { AfterViewInit, Component, Inject, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { finalize, tap } from 'rxjs/operators';
import { debounceTime, distinctUntilChanged, filter, switchMap } from 'rxjs';
import { ProjectType } from '../../../data/models/project-type';
import { Engagement } from '../../../data/models/engagement';
import { ProjectService } from '../../../data/project/project.service';
import { CommonMessages } from '../../../constants/common-messages';
import { StringConstants } from '../../../constants/string-constants';
import { ToasterService } from '../../../api/toaster.service';
import { ProjectsApiService } from '../../../api/projects-api.service';
import { ProjectDetails } from '../../../data/models/project-details';
import { UserService } from 'src/app/data/user/user.service';
import { ValidationService } from 'src/app/common/validation.service';

@Component({
    selector: 'app-create-project-dialog',
    templateUrl: './create-project-dialog.component.html',
    styleUrls: ['./create-project-dialog.component.scss'],
    standalone: false
})
export class CreateProjectDialogComponent implements OnInit, AfterViewInit {
  isEditMode = false;
  projectId?: number;
  projectTypes: ProjectType[] = [];
  filteredEngagements: Engagement[];
  isEngagementsLoading = false;
  displayButton = true;
  preventDoubleClick = false;

  projectForm = new FormGroup({
    title: new FormControl('', [Validators.required, this.validationService.noForbiddenCharactersValidator]),
    type: new FormControl(0, Validators.required),
    engagement: new FormControl('' as any, Validators.required),
    industry: new FormControl('', [this.validationService.noForbiddenCharactersValidator, this.validationService.noWhiteSpaceValidatorForOptionalFields]),
    opportunityCode: new FormControl('', [this.validationService.noForbiddenCharactersValidator]),
    serviceLine: new FormControl(''),
    clientName: new FormControl(''),
    leadPartner: new FormControl('' as any, Validators.required),
    leadDirector: new FormControl('' as any, Validators.required),
    itsLead: new FormControl('' as any, Validators.required),
    projectAdmin: new FormControl('' as any, Validators.required),
  });

  constructor(
    private projectData: ProjectService,
    private projectsApi: ProjectsApiService,
    private toasterService: ToasterService,
    public userService: UserService,
    public validationService: ValidationService,
    public dialogRef: MatDialogRef<CreateProjectDialogComponent>,
    @Inject(MAT_DIALOG_DATA)
    public data: {
      projectId: number;
      isProjectAdmin: boolean;
    }
  ) { }

  async ngOnInit(): Promise<void> {
    if (this.data && this.data.projectId) {
      this.isEditMode = true;
      this.projectId = this.data.projectId;
      const projectDetails = await this.projectsApi.getProjectById(this.projectId);
      this.setFormFromProjectDetails(projectDetails);
    }
    this.projectTypes = await this.projectData.getProjectTypes();
    this.projectForm.controls['engagement'].valueChanges
      .pipe(
        filter((res) => {
          return res !== null && typeof res !== 'object' && res.trim().length >= 4;
        }),
        distinctUntilChanged(),
        debounceTime(500),
        tap(() => {
          this.filteredEngagements = [];
          this.isEngagementsLoading = true;
        }),
        switchMap((val) =>
          this.projectsApi.searchEngagementCodes(val.trim()).pipe(
            finalize(() => {
              // preventing "is loading" disappearing from a concurrent request while cancelling the previous one
              if (val === this.projectForm.controls['engagement'].value) {
                this.isEngagementsLoading = false;
              }
            })
          )
        )
      )
      .subscribe((data: any) => {
        this.filteredEngagements = data;
      });
  }

  ngAfterViewInit(): void {
    if (this.userService.userHasUserRole && !this.data.isProjectAdmin) {
      this.disableForm();
      this.displayButton = false;
    }
  }

  disableForm(): void {
    this.projectForm.disable();
  }

  onChangeOpportunityCode(): void {
    if (this.projectForm.get('opportunityCode')?.value) {
      this.projectForm.get('engagement')?.clearValidators();
    } else {
      this.projectForm.get('engagement')?.setValidators(Validators.required);
    }
    this.projectForm.get('engagement')?.updateValueAndValidity();
  }

  onSelectedEngagement(): void {
    const engagement: any = this.projectForm.controls['engagement'].value;
    this.projectForm.patchValue({
      serviceLine: engagement.serviceLine,
      clientName: engagement.clientName,
      leadPartner: {
        displayName: engagement.engagementPartnerName,
        alternateEmailAddress: engagement.engagementPartnerEmailAddress,
      },
      leadDirector: {
        displayName: engagement.engagementManagerName,
        alternateEmailAddress: engagement.engagementManagerEmailAddress,
      },
    });
    this.validateEngagementObject();
  }

  setFormFromProjectDetails(project: ProjectDetails): void {
    // populate form details with project opportunity first
    if (project.opportunity) {
      this.projectForm.patchValue({
        title: project.projectTitle,
        type: project.projectTypeId,
        opportunityCode: project.opportunity.opportunityCode,
        clientName: project.opportunity.clientName,
        industry: project.industry,
        serviceLine: project.opportunity.serviceLine,
        itsLead: { displayName: project.itsLeadName, alternateEmailAddress: project.itsLeadEmailAddress },
        leadPartner: {
          displayName: project.opportunity.leadPartnerName,
          alternateEmailAddress: project.opportunity.leadPartnerEmail,
        },
        leadDirector: {
          displayName: project.opportunity.leadDirectorName,
          alternateEmailAddress: project.opportunity.leadDirectorEmail,
        },
        projectAdmin: {
          displayName: project.projectAdminName,
          alternateEmailAddress: project.projectAdminEmailAddress,
        },
      });
      this.onChangeOpportunityCode();
    }

    // populate form details with project engagement next
    if (project.engagement) {
      delete project.engagement.engagementCreatedDate;
      delete project.engagement.engagementClosedDate;
      this.projectForm.patchValue({
        title: project.projectTitle.trim(),
        type: project.projectTypeId,
        engagement: project.engagement,
        industry: project.industry,
        itsLead: { displayName: project.itsLeadName, alternateEmailAddress: project.itsLeadEmailAddress },
        projectAdmin: {
          displayName: project.projectAdminName,
          alternateEmailAddress: project.projectAdminEmailAddress,
        },
      });
      this.onSelectedEngagement();
    }
  }

  validateEngagementObject(): void {
    if (
      this.projectForm.controls['engagement'].value === null ||
      typeof this.projectForm.controls['engagement'].value !== 'object'
    ) {
      this.projectForm.patchValue({ leadDirector: '', leadPartner: '' });
      this.projectForm.controls['engagement'].reset();
      this.projectForm.controls['serviceLine'].reset();
      this.projectForm.controls['clientName'].reset();
      this.projectForm.controls['leadPartner'].reset();
      this.projectForm.controls['leadDirector'].reset();
      this.projectForm.get('serviceLine')?.enable();
      this.projectForm.get('clientName')?.enable();
    } else {
      this.projectForm.get('serviceLine')?.disable();
      this.projectForm.get('clientName')?.disable();
    }
    this.projectForm.get('opportunityCode')?.updateValueAndValidity();
  }

  displayEngagementsWith(engagement: any): string {
    if (engagement) {
      return engagement.engagementNumber;
    } else {
      return '';
    }
  }

  addOrUpdateProject(): void {
    this.preventDoubleClick = true;
    this.projectForm.controls['title'].setValue(this.projectForm.controls['title'].value?.trim() || '');
    if (this.projectForm.invalid) {
      return;
    }
    let engagements: any[] = [];
    let opportunity = null;
    // can save both engagement and opportunity at the same time
    if (this.projectForm.controls['engagement'].value) {
      engagements = [
        {
          ...this.projectForm.controls['engagement'].value,
        },
      ];
    }
    if (this.projectForm.controls['opportunityCode'].value) {
      opportunity = {
        opportunityCode: this.projectForm.controls['opportunityCode'].value,
        serviceLine: this.projectForm.controls['serviceLine'].value,
        leadPartnerName: this.projectForm.controls['leadPartner'].value.displayName,
        leadPartnerEmail: this.projectForm.controls['leadPartner'].value.alternateEmailAddress,
        leadDirectorName: this.projectForm.controls['leadDirector'].value.displayName,
        leadDirectorEmail: this.projectForm.controls['leadDirector'].value.alternateEmailAddress,
        clientName: this.projectForm.controls['clientName'].value,
      };
    }
    const createProjectData: any = {
      projectTitle: this.projectForm.controls['title'].value,
      projectTypeId: this.projectForm.controls['type'].value,
      itsLeadName: this.projectForm.controls['itsLead'].value.displayName,
      itsLeadEmailAddress: this.projectForm.controls['itsLead'].value.alternateEmailAddress,
      projectAdminName: this.projectForm.controls['projectAdmin'].value.displayName,
      projectAdminEmailAddress: this.projectForm.controls['projectAdmin'].value.alternateEmailAddress,
      industry: this.projectForm.controls['industry'].value,
      engagements: engagements,
      opportunity: opportunity,
    };
    if (this.isEditMode) {
      createProjectData['projectId'] = this.projectId;
    }

    this.projectsApi.addOrUpdateProject(createProjectData).subscribe({
      next: (id: number) => {
        this.projectData.shouldReloadProjects.next(true);
        this.dialogRef.close();
        this.toasterService.info(
          this.isEditMode ? CommonMessages.PROJECT_SAVED : CommonMessages.PROJECT_CREATED,
          StringConstants.SUCCESS
        );
      },
      error: (err) => {
        const firstError = err.error;
        this.toasterService.info(firstError, StringConstants.ERROR);
        this.preventDoubleClick = false;
      },
    });
  }
}
